import * as moment from "moment";
import React from "react";
import { Col, Row } from "reactstrap";
import PairValue from "./PairValue";

type ComparedField = {
  label: string;
  /** Text shown for a payment; empty string means "not provided". */
  value: (payment: any) => string;
  /** False for fields that always differ, like when the record was added. */
  compare?: boolean;
};

const NOT_PROVIDED = "Not Provided";

const date = (value: any) =>
  value ? moment(new Date(value)).format("DD MMM Y") : "";

const FIELDS: ComparedField[] = [
  {
    label: "MDA & Region",
    value: (p) =>
      p?.mda
        ? [p.mda.name, p.mda.region?.name].filter(Boolean).join(" - ")
        : "",
  },
  { label: "Agency", value: (p) => p?.agency?.name ?? "" },
  { label: "Individual Name", value: (p) => p?.individual?.fullName ?? "" },
  { label: "Position Title", value: (p) => p?.positionTitle ?? "" },
  { label: "Start Date", value: (p) => date(p?.startDate) },
  { label: "End Date", value: (p) => date(p?.endDate) },
  {
    label: "Amount / Month",
    value: (p) =>
      p?.amountPerMonth ? `$${p.amountPerMonth.toLocaleString()}` : "",
  },
  { label: "Created At", value: (p) => date(p?.createdAt), compare: false },
];

const SIDES = [
  {
    key: "payment",
    other: "overlappingPayment",
    title: "First Payment Details",
  },
  {
    key: "overlappingPayment",
    other: "payment",
    title: "Overlapping Payment Details",
  },
] as const;

/**
 * The two payments of an overlap side by side, as shown in the resolve and
 * edit resolution forms. Values that differ between the payments get the same
 * highlight as the overlapping payments table.
 */
const PaymentComparison: React.FC<{ record: any }> = ({ record }) => (
  <Row className="mt-3 mb-4">
    {SIDES.map((side) => (
      <Col sm={6} key={side.key}>
        <h6 className="text-muted text-uppercase fw-semibold mb-3">
          {side.title}
        </h6>
        <div className="table-responsive border border-dashed rounded p-2 mb-3">
          <table className="table table-md table-borderless table-striped mb-0">
            <tbody>
              {FIELDS.map((field) => {
                const text = field.value(record?.[side.key]) || NOT_PROVIDED;
                const other = field.value(record?.[side.other]) || NOT_PROVIDED;
                return (
                  <tr key={field.label}>
                    <td
                      className="fw-medium text-muted"
                      style={{ whiteSpace: "nowrap", width: "30%" }}
                    >
                      {field.label}
                    </td>
                    <td>
                      {field.compare === false ? (
                        text
                      ) : (
                        <PairValue value={text} other={other} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Col>
    ))}
  </Row>
);

export default PaymentComparison;
